import React from "react";
import * as reactRedux from "react-redux";

import { getCheckoutRedirectStep } from "@utils/configuration/aem";
import { CheckoutSteps } from "@utils/enums";
import { useIsBreakPoint } from "@utils/hooks/useIsBreakPoint.hook";

import BreakpointsService from "@services/breakpoint.service";

import { useThunkDispatch } from "@redux/dispatcher";
import { CartModel } from "@redux/modules/api/data/cart/@types/cart.model";
import * as CartModule from "@redux/modules/api/data/cart/cart.module";
import * as CartSelectors from "@redux/modules/api/data/cart/cart.selectors";
import * as CustomerSelectors from "@redux/modules/api/data/customer/customer.selectors";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "@components/General/Button/button.component";

type Props = {
    fullWidth?: boolean;
};

const CheckoutButton = ({ fullWidth }: Props) => {
    const { t } = useGranite();
    const customer = reactRedux.useSelector(CustomerSelectors.getCustomer);
    const cart = reactRedux.useSelector(CartSelectors.getCart);
    const startingCheckout = reactRedux.useSelector(
        CartSelectors.isStartingCheckout,
    );
    const isLoading = reactRedux.useSelector(CartSelectors.isLoading);
    const dispatch = useThunkDispatch();
    const isMobile = useIsBreakPoint(BreakpointsService.matchesMaxXs);

    let itemHasMessages =
        cart?.items.filter(item => !!item.messages.length) || [];

    const determineCheckoutStepBasedOnUser = () => {
        dispatch(CartModule.startCheckout()).then(action => {
            if (action.type === CartModule.APIStatus.SUCCESS) {
                const cart: CartModel = action?.payload;
                if (!cart) {
                    return;
                }

                if (
                    cart.messages.length > 0 ||
                    cart.items.some(
                        item =>
                            item.messages !== null && item.messages.length > 0,
                    ) ||
                    cart.callToActions.length > 0
                ) {
                    return;
                }

                const cartCheckoutStep = cart.checkoutStep;

                if (customer?.guest) {
                    location.assign(
                        getCheckoutRedirectStep(CheckoutSteps.login),
                    );
                } else {
                    location.assign(getCheckoutRedirectStep(cartCheckoutStep));
                }
            }
        });
    };

    return (
        <div>
            {cart && cart.items.length > 0 && (
                <Button
                    test="checkoutButton"
                    disabled={
                        (cart.callToActions || []).length > 0 ||
                        cart.messages.some(e => e.type === "ERROR") ||
                        itemHasMessages.length > 0 ||
                        startingCheckout ||
                        isLoading
                    }
                    size={isMobile ? "md" : "lg"}
                    type="button"
                    color="secondary"
                    fullWidth={fullWidth !== undefined ? fullWidth : true}
                    onClick={() => determineCheckoutStepBasedOnUser()}
                >
                    {t("checkout.cart.checkoutButton.label")}
                </Button>
            )}
            <div className="mt-2">
                <span className="text-muted">
                    <i className="las la-lock pr-1"></i>
                    {t("checkout.cart.checkoutButton.securePayment")}
                </span>
            </div>
        </div>
    );
};

export { CheckoutButton };
